import { useQueryClient } from "@tanstack/react-query";
import { getCapitalCallReallocationDraftDataRetrieveQueryKey } from "gen/endpoints/capital-call-reallocation-draft-data/capital-call-reallocation-draft-data";
import { useFundFamilyEventPendingToCompleteUpdate } from "gen/endpoints/fund-family-event/fund-family-event";
import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import {
  PageWizardProvider,
  PageWizardRoutes,
} from "shared/components/layout/PageWizard";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";
import { rootRoute } from "shared/navigation/Routes";
import { invalidateQueryKeys } from "shared/utils/QueryUtils";

export const CapitalEventReallocationPage: React.FC = () => {
  const { t } = useTranslation(["capitalCall", "common"]);
  const { fundFamilyId, fundFamilyEventId, reallocationFundFamilyEventId } =
    useRequiredStringParams();

  const { mutateAsync: updateEventToComplete } =
    useFundFamilyEventPendingToCompleteUpdate();

  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const backToSummary = useCallback(() => {
    navigate(
      rootRoute({})
        .fundFamilies({})
        .fundFamily({ fundFamilyId })
        .capitalActivity({})
        .capitalActivityEvent({ fundFamilyEventId })
        .index({}).$,
    );
  }, [navigate, fundFamilyId, fundFamilyEventId]);

  const finalizeEvent = useCallback(async () => {
    await updateEventToComplete({
      fundFamilyId,
      fundFamilyEventId: reallocationFundFamilyEventId,
    });
    await invalidateQueryKeys(
      queryClient,
      getCapitalCallReallocationDraftDataRetrieveQueryKey(
        fundFamilyId,
        fundFamilyEventId,
      ),
    );
    backToSummary();
  }, [
    updateEventToComplete,
    fundFamilyId,
    reallocationFundFamilyEventId,
    queryClient,
    fundFamilyEventId,
    backToSummary,
  ]);

  return (
    <PageWizardProvider
      finishTitle={t("capitalCall:reallocation.finalizeReallocation")}
      onFinish={finalizeEvent}
      onExit={backToSummary}
    >
      <PageWizardRoutes />
    </PageWizardProvider>
  );
};
